import { Reveal } from "@components/ui";

interface StepCardProps {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

function StepCard({ number, title, description, icon }: StepCardProps) {
  return (
    <div className="group relative rounded-[16px] md:rounded-[20px] bg-white overflow-hidden flex flex-col items-start p-5 md:py-[28px] md:px-[28px] gap-5 md:gap-6 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
      {/* Watermark number */}
      <span className="absolute top-4 right-5 md:top-5 md:right-6 text-[64px] md:text-[80px] font-bold leading-none text-[#122d63]/[0.04] select-none pointer-events-none">
        {number}
      </span>

      {/* Top row: number badge + icon */}
      <div className="flex items-center gap-3">
        {/* Square number badge */}
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#122d63] group-hover:bg-[#f24d2b] flex items-center justify-center transition-colors duration-300">
          <span className="text-sm md:text-base font-semibold text-white font-albert-sans">
            {number}
          </span>
        </div>

        {/* Icon container */}
        <div className="w-10 h-10 rounded-[12px] bg-[#f24d2b]/[0.08] text-[#f24d2b] flex items-center justify-center">
          {icon}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col items-start gap-3 md:gap-4">
        <h3 className="text-sm md:text-lg lg:text-[28px] tracking-[-0.04em] font-semibold text-[#090d2c] font-albert-sans leading-[1.2]">
          {title}
        </h3>
        <p className="text-[14px] md:text-base lg:text-[20px] tracking-[-0.04em] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.4]">
          {description}
        </p>
      </div>
    </div>
  );
}

const DocumentIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ClipboardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);

const WrenchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const PowerCheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const steps: StepCardProps[] = [
  {
    number: "01",
    title: "Submit Your Application",
    description:
      "Complete our online form and upload the required documents. Our team will verify your request within 24 hours.",
    icon: <DocumentIcon />,
  },
  {
    number: "02",
    title: "Site Assessment & Approval",
    description:
      "A GAZ engineer will inspect your premises to ensure safety and compliance with UAE regulations.",
    icon: <ClipboardIcon />,
  },
  {
    number: "03",
    title: "Installation & Testing",
    description:
      "Our certified technicians install and test the gas network and meters with Civil Defense standards.",
    icon: <WrenchIcon />,
  },
  {
    number: "04",
    title: "Connection Activation",
    description:
      "Once approved, your gas supply is activated and a billing account is created automatically.",
    icon: <PowerCheckIcon />,
  },
];

export function ConnectionStepsSection() {
  return (
    <section className="w-full bg-[#f1f1f1] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 lg:gap-16">
        {/* Badge + Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 md:gap-5">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Process
            </span>
            <h2 className="text-center text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Here's how it works in just four simple steps
            </h2>
          </div>
        </Reveal>

        {/* Step Cards Grid */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          {steps.map((step, index) => (
            <Reveal key={step.number} direction="up" delay={index * 100}>
              <StepCard {...step} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
